// react
import { useEffect, useMemo } from 'react';
import { RouterProvider } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { createPortal } from 'react-dom';

// store
import { useSelector } from 'store';
import { getAccount, getRegisterToken } from 'store/slices/iamSlice';
import { getCareCircleId } from 'store/slices/careCircleSlice';
import { token } from 'store/slices/token';
import {
  useGetSessionCareCircleMemberQuery,
  useGetSessionCareCircleQuery,
  useLazyGetByAccountQuery,
} from 'store/api/careCircle';

// routes
import { createRouter } from 'router';

// components
import {
  AppUpdateDialog,
  OfflineOverlay,
  ThemeProvider,
  Version,
} from '@karehero/llama';

// hooks
import {
  useAppUpdate,
  useAuth,
  useMixpanel,
  useOnlineStatus,
  usePermissions,
  useWindowSize,
} from 'hooks';

// launch darkly
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { createLdContext } from 'launchDarkly';
import { useSignInKindeMutation } from 'store/api/iam';
import useIntercom from 'hooks/useIntercom';
import { Capacitor } from '@capacitor/core';

/**
 * App is the main component of the application.
 */
const App = () => {
  // hooks
  const ldClient = useLDClient();
  const windowSize = useWindowSize();
  const flags = useFlags();
  const account = useSelector(getAccount);

  const careCircleId = useSelector(getCareCircleId);
  const registerToken = useSelector(getRegisterToken);
  const isOnline = useOnlineStatus();
  const [signInKinde] = useSignInKindeMutation();

  const { getToken, isAuthenticated, isLoading, signOut } = useAuth();
  token.setTokenGetter(async () => {
    if (!getToken) return '';
    return await getToken();
  });

  const [getByAccount] = useLazyGetByAccountQuery();
  const { bookCall } = usePermissions();
  const { isDialogVisible, setIsDialogVisible, updateApp } = useAppUpdate();
  useMixpanel();
  useGetSessionCareCircleQuery(undefined, {
    skip:
      !account ||
      !isAuthenticated ||
      isLoading ||
      account.accountRole.name !== 'Member',
  });
  useGetSessionCareCircleMemberQuery(undefined, {
    skip:
      !account ||
      !isAuthenticated ||
      isLoading ||
      account.accountRole.name !== 'Member',
  });

  // effects
  useEffect(() => {
    if (!isAuthenticated || isLoading || account) return;

    signInKinde({ token: registerToken || undefined }).catch((err) => {
      console.error(`Error signing in`);
    });
  }, [account, isAuthenticated, isLoading, registerToken, signInKinde]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !isLoading &&
      account &&
      account.accountRole.name !== 'KareHero Administrator' &&
      account.accountRole.name !== 'Organization Owner'
    ) {
      getByAccount();
    }
  }, [getByAccount, account, isLoading, isAuthenticated]);

  useEffect(() => {
    if (!!account) {
      const newContext = createLdContext(account);
      ldClient?.identify(newContext);
    }
  }, [account, ldClient]);

  // memo
  const isAccountLoading = useMemo(() => {
    if (isLoading) return true;

    if (!account && isAuthenticated) return true;

    return false;
  }, [isLoading, account, isAuthenticated]);

  // global styles
  const Global = createGlobalStyle`
    #hubspot-messages-iframe-container {
      opacity: 0;
      pointer-events: none;
      touch-action: none;
    }
  `;

  return (
    <ThemeProvider>
      <Global />
      <AppWrapper height={windowSize[1]}>
        <Version
          nodeEnvironment={import.meta.env.NODE_ENV}
          buildEnvironment={`${import.meta.env.REACT_APP_BUILD_ENVIRONMENT}`}
          platform={Capacitor.getPlatform()}
          buildVersion={import.meta.env.REACT_APP_BUILD_VERSION}
          buildHash={import.meta.env.REACT_APP_BUILD_HASH}
          onSignOut={signOut}
        />
        {!isOnline &&
          createPortal(
            <OfflineOverlay />,
            document.getElementById('offline-overlay')!,
          )}
        <RouterWrapper>
          <AppUpdateDialog
            isDialogVisible={isDialogVisible}
            setIsDialogVisible={setIsDialogVisible}
            onUpdate={updateApp}
          />
          {!isAccountLoading && (
            <RouterProvider
              router={createRouter({
                isAuthenticated,
                isOnboarded: account?.isOnboarded,
                account,
                careCircleId,
                isCareCircle: flags.careCircle,
                isAttendanceAllowance: flags.attendanceAllowance,
                isFinancialStatus: flags.financialStatus,
                isExternalReferralForm: flags.externalReferralForm,
                isAllowTokenlessRegistration: flags.allowTokenlessRegistration,
                isBookCall: isAuthenticated ? bookCall.isModify : true,
              })}
            />
          )}
        </RouterWrapper>
      </AppWrapper>
    </ThemeProvider>
  );
};

interface AppWrapperProps {
  height: number;
}
const AppWrapper = styled.div<AppWrapperProps>`
  display: flex;
  flex-direction: column;
  height: ${(props) => props.height}px;
  background: ${(props) => props.theme.color.neutral[10]};
  position: relative;
  z-index: 0;
`;

const RouterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

export default App;
